/** Propositions de relance : préparation humaine, ouverture du fil et clôture versionnée. */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@artosera/ui';
import { formatDate, texteEnLangue } from '@artosera/shared';
import { assistantApi, useLectureAssistant, type Relance } from '../../api/assistant-reponse.js';
import { useContact } from '../../api/contacts.js';
import { useConversation } from '../../api/boite-reception.js';
import { useOffre } from '../../api/offres.js';
import { useExposition } from '../../api/expositions.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { useGesteAssistant } from './useGesteAssistant.js';
import styles from './Boite.module.css';

export function RelancesVue() {
  const { galerieSlug } = useShellContext();

  const { t } = useTranslation(['regie', 'commun']);

  const [page, setPage] = useState(1);

  const liste = useLectureAssistant(
    galerieSlug,
    'relances',
    (appel) => assistantApi.relances(galerieSlug, page, appel),
    page,
  );

  return (
    <section className={styles.formulaire}>
      <h2>{t('regie:assistantReponse.relances')}</h2>
      {liste.isPending && <p role="status">{t('commun:chargement')}</p>}
      {liste.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
      {liste.data?.relances.length === 0 && <p>{t('regie:assistantReponse.relancesVides')}</p>}
      {liste.data?.relances.map((p) => (
        <Proposition key={p._id} p={p} />
      ))}
      {liste.data && (
        <div className={styles.gestes}>
          <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            {t('regie:boiteReception.precedent')}
          </Button>
          <span>
            {page} / {Math.max(1, Math.ceil(liste.data.total / 50))}
          </span>
          <Button
            variant="secondary"
            disabled={page * 50 >= liste.data.total}
            onClick={() => setPage(page + 1)}
          >
            {t('regie:boiteReception.suivant')}
          </Button>
        </div>
      )}
    </section>
  );
}

function Proposition({ p }: { p: Relance }) {
  const { galerieSlug, galerie } = useShellContext();

  const { t, i18n } = useTranslation('regie');

  const navigate = useNavigate();

  const contact = useContact(
    galerieSlug,
    galerieALaction(galerie, 'contacts:read') ? p.contactId : null,
  );

  const offre = useOffre(
    galerieSlug,
    p.type === 'offre' &&
      galerieAModule(galerie, 'offres-relances') &&
      galerieALaction(galerie, 'offers:read')
      ? p.objetId
      : null,
  );

  const exposition = useExposition(
    galerieSlug,
    p.type === 'vernissage' && galerieALaction(galerie, 'exhibitions:read') ? p.objetId : null,
  );

  const geste = useGesteAssistant((action: 'preparer' | 'ecarter', appel) =>
    assistantApi.relance(galerieSlug, p._id, action, p.version, appel),
  );

  return (
    <article className={styles.bloc}>
      <h3>{t(`assistantReponse.typesRelance.${p.type}`)}</h3>
      <p>
        {t('assistantReponse.contact')} :{' '}
        {contact.data?.contact.nomAffiche ?? t('assistantReponse.contactIndisponible')}
      </p>
      {p.type === 'conversation' ? (
        <ObjetConversation id={p.objetId} />
      ) : (
        <p>
          {t('assistantReponse.objet')} :{' '}
          {offre.data?.offre.numero ??
            (exposition.data
              ? texteEnLangue(
                  exposition.data.exposition.titre,
                  i18n.language === 'en' ? 'en' : 'fr',
                )
              : t('assistantReponse.objetIndisponible'))}
        </p>
      )}
      <time dateTime={p.proposeeLe}>
        {formatDate(new Date(p.proposeeLe), i18n.language === 'en' ? 'en' : 'fr')}
      </time>
      {galerieALaction(galerie, 'inbox:write') && (
        <div className={styles.gestes}>
          {p.etat === 'preparee' && p.conversationId ? (
            <Button
              variant="secondary"
              onClick={() => void navigate(`/${galerieSlug}/boite-reception/${p.conversationId}`)}
            >
              {t('assistantReponse.ouvrirBrouillon')}
            </Button>
          ) : (
            <Button
              disabled={geste.pending}
              onClick={() => {
                void geste.executer('preparer').then((r) => {
                  if (r?.message)
                    void navigate(`/${galerieSlug}/boite-reception/${r.message.conversationId}`);
                });
              }}
            >
              {t('assistantReponse.preparerRelance')}
            </Button>
          )}
          <Button
            variant="secondary"
            disabled={geste.pending}
            onClick={() => void geste.executer('ecarter')}
          >
            {t('assistantReponse.ecarter')}
          </Button>
        </div>
      )}
      {geste.pending && <p role="status">{t('assistantReponse.attente')}</p>}
      {geste.erreur && <p role="alert">{geste.erreur}</p>}
    </article>
  );
}

function ObjetConversation({ id }: { id: string }) {
  const { galerieSlug } = useShellContext();

  const { t } = useTranslation('regie');

  const conversation = useConversation(galerieSlug, id);

  return (
    <p>
      {t('assistantReponse.objet')} :{' '}
      {conversation.data
        ? conversation.data.conversation.sujet || t('boiteReception.sansSujet')
        : t('assistantReponse.objetIndisponible')}
    </p>
  );
}
